import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { 
  CheckCircle2, 
  FileSearch, 
  Target, 
  TrendingUp,
  Sparkles,
  BarChart3,
  Lightbulb,
  Award,
  Video,
  MessageSquare,
  Mic,
  BookOpen,
  ListChecks,
  Zap,
  LineChart,
  Brain,
  FileText,
  ArrowRight
} from 'lucide-react';

// Custom hook for intersection observer animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

const TalentProducts = () => {
  const { t } = useTranslation();
  
  // Animation refs for each section
  const [freeCvRef, freeCvVisible] = useScrollAnimation(0.15);
  const [cvRef, cvVisible] = useScrollAnimation(0.15);
  const [interviewRef, interviewVisible] = useScrollAnimation(0.15);
  const [growthRef, growthVisible] = useScrollAnimation(0.15);

  return (
    <>
      {/* Free AI CV Builder Section — isbulsun.com */}
      <section
        ref={freeCvRef}
        id="free-cv-builder-section"
        className="section-spacing bg-gradient-to-br from-teal-50/60 via-white to-emerald-50/40 overflow-hidden"
      >
        <div className="container-custom px-4 sm:px-6">
          <a
            href="https://isbulsun.com"
            target="_blank"
            rel="noopener noreferrer"
            className={`group block rounded-2xl sm:rounded-3xl bg-white shadow-xl border border-gray-100 p-6 sm:p-8 lg:p-12 transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_30px_60px_-15px_rgba(16,185,129,0.3)] ${
              freeCvVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="grid lg:grid-cols-[1fr_auto] gap-6 sm:gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-emerald-100 text-emerald-700 text-xs sm:text-sm font-label mb-4 sm:mb-6">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                  {t('talentProducts.freeCvBadge')}
                </div>

                <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-3 sm:mb-4">
                  {t('talentProducts.freeCvTitle')}
                </h2>

                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-5 sm:mb-6">
                  {t('talentProducts.freeCvDesc')}
                </p>

                <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                  {[
                    t('talentProducts.freeCvPoint1'),
                    t('talentProducts.freeCvPoint2'),
                    t('talentProducts.freeCvPoint3'),
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 sm:gap-2">
                      <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                  <span className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-button text-white shadow-lg transition-all duration-300 group-hover:shadow-xl">
                    {t('talentProducts.freeCvCta')}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    {t('talentProducts.freeCvHint')}
                  </span>
                </div>
              </div>

              {/* Visual */}
              <div className="hidden lg:block relative">
                <div className="w-56 h-72 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 shadow-2xl rotate-3 transition-transform duration-500 group-hover:rotate-6" />
                <div className="absolute inset-0 -rotate-3 transition-transform duration-500 group-hover:-rotate-6">
                  <div className="w-56 h-72 rounded-2xl bg-white shadow-xl border border-gray-100 p-5 flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 w-24 rounded-full bg-gray-300" />
                        <div className="h-2 w-16 rounded-full bg-gray-200" />
                      </div>
                    </div>
                    <div className="space-y-2 flex-1">
                      <div className="h-2 w-full rounded-full bg-gray-100" />
                      <div className="h-2 w-5/6 rounded-full bg-gray-100" />
                      <div className="h-2 w-11/12 rounded-full bg-gray-100" />
                      <div className="h-2 w-2/3 rounded-full bg-gray-100" />
                      <div className="h-2 w-full rounded-full bg-gray-100" />
                      <div className="h-2 w-4/5 rounded-full bg-gray-100" />
                      <div className="h-2 w-1/2 rounded-full bg-emerald-200" />
                    </div>
                    <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                      <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span className="text-xs font-semibold text-emerald-700">
                        {t('talentProducts.freeCvBadge')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* CV Analysis Section */}
      <section 
        ref={cvRef}
        id="cv-analysis-section" 
        className="section-spacing bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 overflow-hidden"
      >
        <div className="container-custom px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div className={`order-2 lg:order-1 transition-all duration-700 ${cvVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div 
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-emerald-100 text-emerald-700 text-xs sm:text-sm font-label mb-4 sm:mb-6"
                style={{ transitionDelay: '100ms' }}
              >
                <FileSearch className="w-3 h-3 sm:w-4 sm:h-4" />
                {t('talentProducts.cvAnalysisBadge')}
              </div>
              
              <h2 
                className={`font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-3 sm:mb-4 transition-all duration-700 ${cvVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '200ms' }}
              >
                {t('talentProducts.cvAnalysisTitle')}
              </h2>
              
              <p 
                className={`text-base sm:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6 transition-all duration-700 ${cvVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '300ms' }}
              >
                {t('talentProducts.cvAnalysisDescLong')}
              </p>

              {/* Features Grid */}
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-subheading text-sm sm:text-base text-gray-900 mb-1">{t('talentProducts.cvFeature1Title')}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{t('talentProducts.cvFeature1Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">{t('talentProducts.cvFeature2Title')}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{t('talentProducts.cvFeature2Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">{t('talentProducts.cvFeature3Title')}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{t('talentProducts.cvFeature3Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">{t('talentProducts.cvFeature4Title')}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{t('talentProducts.cvFeature4Desc')}</p>
                  </div>
                </div>
              </div>

              {/* Checklist */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                {[
                  t('talentProducts.cvCheck1'),
                  t('talentProducts.cvCheck2'),
                  t('talentProducts.cvCheck3'),
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual */}
            <div className={`order-1 lg:order-2 relative transition-all duration-700 ${cvVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-10 scale-95'}`} style={{ transitionDelay: '400ms' }}>
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-[0_30px_60px_-15px_rgba(16,185,129,0.25)] transition-all duration-500">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                        <FileSearch className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm sm:text-base text-gray-900">{t('talentProducts.cvMockTitle')}</h3>
                        <p className="text-xs sm:text-sm text-gray-500">{t('talentProducts.cvMockSubtitle')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl sm:text-2xl font-bold text-emerald-600">87<span className="text-base sm:text-lg">/100</span></div>
                      <p className="text-xs text-gray-500">{t('talentProducts.cvMockScore')}</p>
                    </div>
                  </div>

                  {/* Score Bars */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{t('talentProducts.cvScoreATS')}</span>
                        <span className="font-semibold text-gray-900">92%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{t('talentProducts.cvScoreImpact')}</span>
                        <span className="font-semibold text-gray-900">78%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{t('talentProducts.cvScoreReadability')}</span>
                        <span className="font-semibold text-gray-900">95%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full" style={{ width: '95%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{t('talentProducts.cvScoreRole')}</span>
                        <span className="font-semibold text-gray-900">83%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-400 to-pink-500 rounded-full" style={{ width: '83%' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Suggestions */}
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-800">{t('talentProducts.cvSuggestionTitle')}</span>
                    </div>
                    <p className="text-sm text-amber-700">{t('talentProducts.cvSuggestionText')}</p>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-lg p-4 hidden md:block float-slow">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('talentProducts.cvFloatLabel')}</p>
                      <p className="text-sm font-bold text-gray-900">+23%</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl shadow-lg p-4 text-white hidden md:block float-medium">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <span className="text-sm font-semibold">{t('talentProducts.cvFloatAI')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interview Prep Section */}
      <section 
        ref={interviewRef}
        id="interview-prep-section" 
        className="py-12 sm:py-16 md:py-20 lg:py-28 bg-gradient-to-br from-indigo-50/50 via-white to-sky-50/30 overflow-hidden"
      >
        <div className="container-custom px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
            {/* Visual - Left side for this section */}
            <div className={`relative transition-all duration-700 ${interviewVisible ? 'opacity-100 -translate-x-0 scale-100' : 'opacity-0 -translate-x-10 scale-95'}`} style={{ transitionDelay: '200ms' }}>
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-2xl p-6 lg:p-8 border border-gray-100 hover:shadow-[0_30px_60px_-15px_rgba(99,102,241,0.25)] transition-all duration-500">
                  {/* Video Preview Mock */}
                  <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl aspect-video mb-6 overflow-hidden">
                    {/* Avatar/Video placeholder */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <Mic className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    
                    {/* Recording indicator */}
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 rounded-full px-3 py-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      <span className="text-white text-xs font-medium">{t('talentProducts.interviewRecording')}</span>
                    </div>

                    {/* Timer */}
                    <div className="absolute top-4 right-4 bg-black/50 rounded-full px-3 py-1.5">
                      <span className="text-white text-xs font-mono">02:34</span>
                    </div>

                    {/* Question overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <p className="text-white text-sm">{t('talentProducts.interviewQuestion')}</p>
                    </div>
                  </div>

                  {/* Mode Selector */}
                  <div className="flex gap-2 mb-6">
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 text-white font-medium">
                      <Video className="w-4 h-4" />
                      {t('talentProducts.interviewModeVideo')}
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-100 text-gray-600 font-medium">
                      <Mic className="w-4 h-4" />
                      {t('talentProducts.interviewModeVoice')}
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-100 text-gray-600 font-medium">
                      <MessageSquare className="w-4 h-4" />
                      {t('talentProducts.interviewModeText')}
                    </button>
                  </div>

                  {/* Live Feedback */}
                  <div className="bg-gradient-to-r from-indigo-50 to-sky-50 rounded-xl p-4 border border-indigo-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Brain className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-semibold text-indigo-800">{t('talentProducts.interviewFeedbackTitle')}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <div className="text-xl font-bold text-indigo-600">8.5</div>
                        <p className="text-xs text-gray-500">{t('talentProducts.interviewScoreClarity')}</p>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-emerald-600">9.2</div>
                        <p className="text-xs text-gray-500">{t('talentProducts.interviewScoreStructure')}</p>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-amber-600">7.8</div>
                        <p className="text-xs text-gray-500">{t('talentProducts.interviewScoreConfidence')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -left-4 bg-white rounded-xl shadow-lg p-3 hidden md:block float-medium">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{t('talentProducts.interviewFloatInstant')}</span>
                  </div>
                </div>

                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl shadow-lg p-4 text-white hidden md:block float-slow">
                  <div className="text-center">
                    <div className="text-2xl font-bold">150+</div>
                    <p className="text-xs opacity-90">{t('talentProducts.interviewFloatQuestions')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`transition-all duration-700 ${interviewVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{ transitionDelay: '400ms' }}>
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 text-indigo-700 text-sm font-label mb-6"
              >
                <Sparkles className="w-4 h-4" />
                {t('talentProducts.interviewPrepBadge')}
              </div>
              
              <h2 
                className={`font-heading text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4 transition-all duration-700 ${interviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '500ms' }}
              >
                {t('talentProducts.interviewPrepTitle')}
              </h2>
              
              <p 
                className={`text-lg text-gray-600 leading-relaxed mb-6 transition-all duration-700 ${interviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '600ms' }}
              >
                {t('talentProducts.interviewPrepDescLong')}
              </p>

              {/* Features Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Video className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.interviewFeature1Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.interviewFeature1Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.interviewFeature2Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.interviewFeature2Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.interviewFeature3Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.interviewFeature3Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <ListChecks className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.interviewFeature4Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.interviewFeature4Desc')}</p>
                  </div>
                </div>
              </div>

              {/* Checklist */}
              <div className="flex flex-wrap items-center gap-4">
                {[
                  t('talentProducts.interviewCheck1'),
                  t('talentProducts.interviewCheck2'),
                  t('talentProducts.interviewCheck3'),
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role-Based Growth Section */}
      <section 
        ref={growthRef}
        id="role-growth-section" 
        className="py-12 sm:py-16 md:py-20 lg:py-28 bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30 overflow-hidden"
      >
        <div className="container-custom px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div className={`order-2 lg:order-1 transition-all duration-700 ${growthVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-sm font-label mb-6"
                style={{ transitionDelay: '100ms' }}
              >
                <TrendingUp className="w-4 h-4" />
                {t('talentProducts.roleGrowthBadge')}
              </div>
              
              <h2 
                className={`font-heading text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4 transition-all duration-700 ${growthVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '200ms' }}
              >
                {t('talentProducts.roleGrowthTitle')}
              </h2>
              
              <p 
                className={`text-lg text-gray-600 leading-relaxed mb-6 transition-all duration-700 ${growthVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '300ms' }}
              >
                {t('talentProducts.roleGrowthDescLong')}
              </p>

              {/* Features Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <LineChart className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.growthFeature1Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.growthFeature1Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.growthFeature2Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.growthFeature2Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <ListChecks className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.growthFeature3Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.growthFeature3Desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('talentProducts.growthFeature4Title')}</h4>
                    <p className="text-sm text-gray-600">{t('talentProducts.growthFeature4Desc')}</p>
                  </div>
                </div>
              </div>

              {/* Checklist */}
              <div className="flex flex-wrap items-center gap-4">
                {[
                  t('talentProducts.growthCheck1'),
                  t('talentProducts.growthCheck2'),
                  t('talentProducts.growthCheck3'),
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual */}
            <div className={`order-1 lg:order-2 relative transition-all duration-700 ${growthVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-10 scale-95'}`} style={{ transitionDelay: '400ms' }}>
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-2xl p-6 lg:p-8 border border-gray-100 hover:shadow-[0_30px_60px_-15px_rgba(251,191,36,0.25)] transition-all duration-500">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{t('talentProducts.growthMockTitle')}</h3>
                        <p className="text-sm text-gray-500">{t('talentProducts.growthMockSubtitle')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Skills Radar Mock */}
                  <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{t('talentProducts.skillTechnical')}</span>
                            <span className="font-semibold text-amber-700">75%</span>
                          </div>
                          <div className="h-2 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '75%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{t('talentProducts.skillCommunication')}</span>
                            <span className="font-semibold text-amber-700">88%</span>
                          </div>
                          <div className="h-2 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '88%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{t('talentProducts.skillLeadership')}</span>
                            <span className="font-semibold text-amber-700">62%</span>
                          </div>
                          <div className="h-2 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '62%' }}></div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{t('talentProducts.skillProblem')}</span>
                            <span className="font-semibold text-amber-700">80%</span>
                          </div>
                          <div className="h-2 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '80%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{t('talentProducts.skillAdaptability')}</span>
                            <span className="font-semibold text-amber-700">91%</span>
                          </div>
                          <div className="h-2 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '91%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{t('talentProducts.skillTeamwork')}</span>
                            <span className="font-semibold text-amber-700">85%</span>
                          </div>
                          <div className="h-2 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: '85%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Weekly Tasks */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <ListChecks className="w-4 h-4 text-amber-600" />
                      {t('talentProducts.weeklyTasksTitle')}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span className="text-sm text-gray-700 line-through">{t('talentProducts.task1')}</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                        <div className="w-5 h-5 rounded-full border-2 border-amber-400"></div>
                        <span className="text-sm text-gray-700">{t('talentProducts.task2')}</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                        <span className="text-sm text-gray-700">{t('talentProducts.task3')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-lg p-4 hidden md:block float-slow">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600">+15%</div>
                    <p className="text-xs text-gray-500">{t('talentProducts.growthFloatProgress')}</p>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow-lg p-4 text-white hidden md:block float-medium">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    <span className="text-sm font-semibold">{t('talentProducts.growthFloatResources')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TalentProducts;

